"use client";

import { useTranslations } from "use-intl";
import { ActiveNotificationIcon, ArrowForwardIcon, CheckIcon } from "@/components/appIcons";
import { TelegramIcon } from "@/components/icons/telegram-icon";
import { WhatsAppIcon } from "@/components/icons/whatsapp-icon";
import { Link } from "@/components/link";
import { WhatsAppFormatJoinLinks } from "@/components/messaging/WhatsAppFormatJoinLinks";
import { BrowserPushControl } from "@/components/notifications/browser-push-control";
import { TelegramCompactConnectionAction } from "@/components/telegram/TelegramCompactConnectionAction";
import { useDistributionAvailability } from "@/hooks/use-distribution-availability";
import { useTelegramConnectionAction } from "@/hooks/use-telegram-connection-action";
const TELEGRAM_POINTS = ["Taste", "Delivery", "Best"] as const;
const WHATSAPP_POINTS = ["Groups", "Shared", "Best"] as const;
function AlertsPage() {
  const t = useTranslations("alerts");
  const tWhatsApp = useTranslations("whatsapp");
  const telegramConnection = useTelegramConnectionAction("/alerts");
  const distributionQuery = useDistributionAvailability();
  const destinationsQuery = {
    data: distributionQuery.data?.whatsapp,
    isLoading: distributionQuery.isLoading
  };
  const destinations = destinationsQuery.data?.destinations;
  const hasFormatDestination = Boolean(destinations?.vinyl || destinations?.cd);
  return <main className="overflow-hidden bg-[#121513] text-[#f7f3ea]">
      <section className="relative isolate border-b border-white/10 px-4 py-10 sm:px-8 sm:py-14 lg:px-12 lg:py-12">
        <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true" style={{
        background: "radial-gradient(circle at 16% 24%, rgba(34,158,217,.17), transparent 30%), radial-gradient(circle at 84% 22%, rgba(37,211,102,.15), transparent 30%)"
      }} />
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(22rem,.85fr)] lg:items-end lg:gap-14">
            <h1 className="max-w-3xl text-balance text-4xl font-black leading-[1.04] tracking-[-0.035em] sm:text-5xl lg:text-6xl">
              {t("title")}
            </h1>
            <div>
              <p className="max-w-xl text-pretty text-base leading-7 text-[#cbc7be] sm:text-lg">
                {t("description")}
              </p>
              <div className="mt-5 flex flex-wrap items-baseline justify-between gap-2 border-t border-white/10 pt-4">
                <h2 className="text-lg font-black tracking-tight sm:text-xl">
                  {t("optionsTitle")}
                </h2>
                <p className="text-sm font-semibold text-[#aaa79f]">{t("optionsHint")}</p>
              </div>
            </div>
          </div>

          <nav aria-label={t("optionsNavLabel")} className="mt-4 grid grid-cols-3 gap-2 lg:hidden">
            <a className="flex min-h-16 flex-col items-center justify-center gap-1 rounded-lg bg-white/[0.07] px-2 py-2 text-center text-xs font-bold text-[#f4a17f] transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e66a3a]" href="#alerts-notifications">
              <ActiveNotificationIcon className="text-xl" decorative />
              {t("notificationsShort")}
            </a>
            <a className="flex min-h-16 flex-col items-center justify-center gap-1 rounded-lg bg-white/[0.07] px-2 py-2 text-center text-xs font-bold text-[#63b9e6] transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#229ED9]" href="#alerts-telegram">
              <TelegramIcon className="size-5" />
              {t("telegramShort")}
            </a>
            <a className="flex min-h-16 flex-col items-center justify-center gap-1 rounded-lg bg-white/[0.07] px-2 py-2 text-center text-xs font-bold text-[#5ce58c] transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]" href="#alerts-whatsapp">
              <WhatsAppIcon className="size-5" />
              {t("whatsappShort")}
            </a>
          </nav>

          <div className="mt-7 grid overflow-hidden rounded-2xl border border-white/10 bg-white/10 lg:grid-cols-3 lg:gap-px">
            <BrowserPushControl surface="alerts" />
            <AlertOption channel="telegram" label={t("telegramLabel")} title={t("telegramTitle")} description={t("telegramDescription")} points={TELEGRAM_POINTS.map(point => t(`telegramPoint${point}`))} action={<TelegramCompactConnectionAction {...telegramConnection} label={t("telegramCta")} onConnect={telegramConnection.onConnect} />} />
            <AlertOption channel="whatsapp" label={t("whatsappLabel")} title={t("whatsappTitle")} description={t("whatsappDescription")} points={WHATSAPP_POINTS.map(point => t(`whatsappPoint${point}`))} action={destinationsQuery.isLoading ? <div className="h-24 w-full animate-pulse rounded-xl bg-white/10 motion-reduce:animate-none" aria-hidden="true" /> : hasFormatDestination ? <WhatsAppFormatJoinLinks destinations={destinations} surface="alerts_page" variant="compact" /> : destinationsQuery.data?.available ? <Link className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-5 py-3 font-black text-[#07170d] transition hover:bg-[#36df74] sm:w-fit" to="/whatsapp">
                    {t("whatsappChooseGroup")}
                    <ArrowForwardIcon decorative />
                  </Link> : <p className="text-sm font-semibold text-amber-100">
                    {tWhatsApp("unavailableTitle")}
                  </p>} />
          </div>
        </div>
      </section>
    </main>;
}
function AlertOption({
  channel,
  label,
  title,
  description,
  points,
  action
}: {
  channel: "telegram" | "whatsapp";
  label: string;
  title: string;
  description: string;
  points: string[];
  action: ReactNode;
}) {
  const telegram = channel === "telegram";
  const accent = telegram ? "#229ED9" : "#25D366";
  const Icon = telegram ? TelegramIcon : WhatsAppIcon;
  return <article className="flex scroll-mt-20 flex-col bg-[#171b18] p-6 sm:p-7" id={`alerts-${channel}`}>
      <div className="flex items-center gap-3">
        <span className="flex size-11 items-center justify-center rounded-full text-white" style={{
        backgroundColor: accent
      }}>
          <Icon className="size-6" />
        </span>
        <p className="text-xs font-black uppercase tracking-[0.16em]" style={{
        color: accent
      }}>
          {label}
        </p>
      </div>
      <h2 className="mt-6 text-2xl font-black tracking-tight">{title}</h2>
      <p className="mt-3 max-w-xl text-sm leading-6 text-[#aaa79f]">{description}</p>
      <div className="mt-6">{action}</div>
      <ul className="mt-6 space-y-2">
        {points.map(point => <li className="flex items-start gap-3 text-sm leading-6 text-[#d5d2ca]" key={point}>
            <CheckIcon className="mt-0.5 text-lg" style={{
          color: accent
        }} decorative />
            <span>{point}</span>
          </li>)}
      </ul>
    </article>;
}
export { AlertsPage as component };